import { Checkbox, Icon } from '@aqarmap/aqarmap-ui';
import { useSortable } from '@dnd-kit/sortable';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  useDeletePhotosMutation,
  useSetMainPhotoMutation,
  useUploadPhotosMutation,
} from '~/redux/api/userApiSlice';

type PhotoItem = {
  id: string;
  file: File;
  photoId?: string;
};

type UploadedPhotosType = {
  id: string;
  src: string;
};

const SortablePhotoCard = ({
  isChecked,
  setFiles,
  setSelectedPhotos,
  files,
  file,
  uploadedPhotos,
  setUploadedPhotos,
  src,
  isUploaded = false,
  setListingStatusId,
  id = '',
}: {
  isChecked: boolean;
  files: PhotoItem[];
  file: PhotoItem;
  setFiles: React.Dispatch<React.SetStateAction<PhotoItem[]>>;
  setSelectedPhotos: React.Dispatch<React.SetStateAction<string[]>>;
  uploadedPhotos: UploadedPhotosType[];
  setUploadedPhotos: React.Dispatch<React.SetStateAction<UploadedPhotosType[]>>;
  src: string;
  isUploaded?: boolean;
  setListingStatusId: React.Dispatch<React.SetStateAction<number | null>>;
  id?: string;
}) => {
  const { t } = useTranslation('app');
  const hasUploaded = useRef(false);
  const { locale, id: listingId } = useParams();
  const [isMainPhoto, setIsMainPhoto] = React.useState(false);
  const [loadingPercentage, setLoadingPercentage] = React.useState(0);
  const [isRotating, setIsRotating] = React.useState(false);
  const [uploadPhoto, { isError }] = useUploadPhotosMutation();
  const [deletePhoto, { isLoading: isLoadingDeletePhoto }] =
    useDeletePhotosMutation();
  const [setMainPhoto, { isLoading: isLoadingSetMainPhoto }] =
    useSetMainPhotoMutation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    cursor: 'grab',
    zIndex: isDragging ? 999 : 'auto',
  };

  const handleDeletePhoto = () => {
    const uploadedPhoto = uploadedPhotos.find((p) => p.id === file.id);
    if (!uploadedPhoto) {
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      setSelectedPhotos((prev) =>
        prev.filter((selectedId) => selectedId !== file.id),
      );
      return;
    }
    if (uploadedPhoto.id) {
      deletePhoto({
        id: listingId,
        formData: { ids: [uploadedPhoto.id] },
      })
        .unwrap()
        .then((e) => {
          setFiles((prev) => prev.filter((f) => f.id !== file.id));
          setSelectedPhotos((prev) =>
            prev.filter((selectedId) => selectedId !== file.id),
          );
          setUploadedPhotos((prev) =>
            prev.filter((uploadedPhoto) => uploadedPhoto.id !== file.id),
          );
          setListingStatusId(e.listing?.status);
        })
        .catch((error) => {
          console.error('Error deleting photo:', error);
        });
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) setSelectedPhotos((prev) => [...prev, file.id]);
    else setSelectedPhotos((prev) => prev.filter((i) => i !== file.id));
  };

  const handleRotatePhoto = async () => {
    setIsRotating(true);
    const photoToRotate = uploadedPhotos.find((f) => f.id === file.id);
    if (!photoToRotate || !photoToRotate.src) return;

    const img = new Image();
    img.src = photoToRotate.src;
    img.crossOrigin = 'anonymous';

    img.onload = async () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) return;

      canvas.width = img.height;
      canvas.height = img.width;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((-90 * Math.PI) / 180);
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      canvas.toBlob(
        async (blob) => {
          if (!blob) return;

          const index = uploadedPhotos.findIndex((f) => f.id === file.id);
          const formData = new FormData();
          formData.append(`photos[${index}][file]`, blob);
          formData.append(`photos[${index}][id]`, photoToRotate.id);

          uploadPhoto({ formData, id: listingId })
            .then(() => {
              const foundPhoto = uploadedPhotos.find(
                (p) => p.id === photoToRotate.id,
              );
              if (!foundPhoto) return;
              const updatedPhoto = {
                ...foundPhoto,
                src: URL.createObjectURL(blob),
              };
              const updatedPhotos = uploadedPhotos.map((p) =>
                p.id === updatedPhoto.id ? updatedPhoto : p,
              );
              setUploadedPhotos(updatedPhotos);
            })
            .finally(() => setIsRotating(false));
        },
        'image/jpeg',
        1.0,
      );
    };
  };

  const setMainPhotoLocally = () => {
    const updatedFiles = [...uploadedPhotos];
    const index = updatedFiles.findIndex((f) => f.id === file.id);
    if (index > -1) {
      const [selectedFile] = updatedFiles.splice(index, 1);
      updatedFiles.unshift(selectedFile);
    }
    setUploadedPhotos(updatedFiles);
  };

  const handleSetMainPhoto = async () => {
    const uploadedPhoto = uploadedPhotos.find((p) => p.id === file.id);
    const res = await setMainPhoto({ listingId, photoId: uploadedPhoto?.id });
    if ('error' in res) return;
    if ('data' in res) setMainPhotoLocally();
  };

  const handleUploadPhoto = () => {
    const index = files.findIndex((f) => f.id === file.id);
    const formData = new FormData();
    formData.append(`photos[${index}][file]`, file.file);
    uploadPhoto({ formData, id: listingId })
      .unwrap()
      .then((e) => {
        setLoadingPercentage(100);
        setFiles((prev) => prev.filter((f) => f.id !== file.id));
        const uploadedPhotosData = e.listing?.photos.map((photo: any) => ({
          id: `${photo.id}`,
          src: photo.photo.thumbnails.thumb,
        }));
        setUploadedPhotos(uploadedPhotosData);
        setListingStatusId(e.listing?.status);
      })
      .catch((error) => {
        console.error('Error uploading photo:', error);
      });
  };

  useEffect(() => {
    if (hasUploaded.current) return;
    const photo = uploadedPhotos.find((p) => p.id === file.id);
    if (!photo && !isUploaded) {
      handleUploadPhoto();
      hasUploaded.current = true;
    }
  }, []);

  useEffect(() => {
    if (loadingPercentage >= 100) return;

    const interval = setInterval(() => {
      setLoadingPercentage((prevPercentage) => {
        if (prevPercentage === 90) return 90;
        return prevPercentage + 5;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [loadingPercentage]);

  useEffect(() => {
    //setIsMainPhoto(file.id === mainPhotoId);
    setIsMainPhoto(
      uploadedPhotos.length > 0 ? uploadedPhotos[0]?.id === file.id : false,
    );
  }, [uploadedPhotos]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${isMainPhoto ? 'border-[2px] border-blue_light_1' : 'border border-gray'} relative flex flex-col w-full sm:w-[200px] h-[156px] p-1.5x  bg-gray_light_1 rounded-[10px] overflow-hidden`}
    >
      <div {...attributes} {...listeners}>
        <img
          src={src}
          alt=""
          className="absolute top-0 left-0 w-full h-full object-cover"
        />
      </div>
      <div className="absolute top-x left-0 px-x w-full flex gap-x justify-between">
        <Checkbox
          size="medium"
          field={{
            name: 'selectAll',
            className: 'bg-white',
            checked: isChecked,
            onChange: handleCheckboxChange,
          }}
        />
        <div className="flex items-center gap-x w-max">
          {!isMainPhoto && (
            <>
              {isLoadingSetMainPhoto ? (
                <span className="loading loading-spinner loading-sm bg-blue"></span>
              ) : (
                <button
                  className="bg-white rounded-[4px] w-[24px] h-[24px] flex items-center justify-center"
                  onClick={() => handleSetMainPhoto()}
                >
                  <Icon name="home" size="18px" className="text-blue" />
                </button>
              )}
            </>
          )}
          {isRotating ? (
            <span className="loading loading-spinner loading-sm bg-gray__dark_2"></span>
          ) : (
            <button
              className="bg-white rounded-[4px] w-[24px] h-[24px] flex items-center justify-center"
              onClick={handleRotatePhoto}
            >
              <Icon name="rotate" size="20px" className="text-gray__dark_2" />
            </button>
          )}
          <button
            className="bg-white rounded-[4px] w-[24px] h-[24px] flex items-center justify-center"
            onClick={handleDeletePhoto}
          >
            {isLoadingDeletePhoto ? (
              <span className="loading loading-spinner loading-sm bg-red_light"></span>
            ) : (
              <Icon name="trash" size="20px" className="text-red_light" />
            )}
          </button>
        </div>
      </div>
      {isMainPhoto && (
        <p
          className={`${locale === 'ar' ? 'custom-gradient-90deg' : 'custom-gradient-270deg'} rounded-[4px] text-[8px] font-[400] leading-[24px] text-white absolute bottom-x start-x h-[24px] bg-black w-max pe-2x ps-0.5x`}
        >
          {t('mainPhoto')}
        </p>
      )}
      {loadingPercentage < 100 && !isError && !isUploaded && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-65 flex justify-center items-center">
          <div className="w-[80px] h-[4px] rounded-full bg-white">
            <div
              className="h-full bg-blue rounded-full transition-all duration-100"
              style={{ width: `${loadingPercentage}%` }}
            ></div>
          </div>
        </div>
      )}
      {isError && (
        <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-65 flex justify-center items-center gap-1">
          <p className="text-white text-link">{t('sizeMoreThan2MG')}</p>
          <button
            className="bg-white rounded-[4px] w-[24px] h-[24px] flex items-center justify-center absolute top-2 start-2"
            onClick={handleDeletePhoto}
          >
            <Icon name="trash" size="20px" className="text-red_light" />
          </button>
        </div>
      )}
    </div>
  );
};

export default SortablePhotoCard;
